import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import './InterventionPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InterventionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const serviceId = searchParams.get('servico');
  
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [servicesRes, contentRes] = await Promise.all([
          axios.get(`${API}/cms/public/services`),
          axios.get(`${API}/cms/public/content/intervencao`)
        ]);
        
        setServices(servicesRes.data);
        
        // Extract hero content
        const heroContent = contentRes.data.find(c => c.section === 'hero');
        setPageContent(heroContent?.content || {});
        
        // If we have a serviceId, find that specific service
        if (serviceId) {
          const selectedService = servicesRes.data.find(s => s.slug === serviceId);
          setService(selectedService || null);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
      setLoading(false);
    };
    
    loadData();
  }, [serviceId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (loading) {
    return (
      <div className="intervention-page">
        <Header />
        <div style={{ padding: '200px 20px', textAlign: 'center', color: '#666' }}>
          A carregar...
        </div>
        <Footer />
      </div>
    );
  }

  // Grid view - list of all services
  if (!service) {
    return (
      <div className="intervention-page" data-testid="intervention-page">
        <SEO 
          title="Áreas de Intervenção"
          description="Conheça as áreas de intervenção da AASPS - reabilitação psicossocial, apoio psicológico, apoio social e outras abordagens na área da saúde mental."
          keywords="intervenção, reabilitação psicossocial, apoio psicológico, apoio social, saúde mental, AASPS"
          url="/intervencao"
        />
        <Header />
        
        <div className="intervention-hero">
          <div className="intervention-hero-content">
            <h1>{pageContent.title || 'Áreas de Intervenção'}</h1>
            <p>{pageContent.description || 'O nosso trabalho assenta em abordagens validadas e atualizadas na área da saúde mental e da reabilitação psicossocial.'}</p>
          </div>
        </div>

        <section className="intervention-grid-section">
          <div className="intervention-grid-container">
            <h2>Os Nossos Serviços</h2>
            <div className="intervention-grid">
              {services.map((srv) => (
                <div 
                  key={srv.slug} 
                  className="intervention-card"
                  onClick={() => navigate(`/intervencao?servico=${srv.slug}`)}
                  data-testid={`card-${srv.slug}`}
                >
                  <img 
                    src={srv.image} 
                    alt={srv.title}
                    className="intervention-card-image"
                  />
                  <div className="intervention-card-content">
                    <h3>{srv.title}</h3>
                    <p>{srv.hero_description?.substring(0, 100)}...</p>
                    <span className="read-more">Saber mais →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-container">
            <h2>Precisa de Apoio?</h2>
            <p>Entre em contacto connosco para saber como podemos ajudar.</p>
            <button className="cta-button" onClick={() => navigate('/contactos')}>
              FALE CONNOSCO
            </button>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Detail view - single service
  return (
    <div className="intervention-page" data-testid="intervention-detail-page">
      <SEO 
        title={service?.title || 'Área de Intervenção'}
        description={service?.description?.substring(0, 160) || 'Detalhes da área de intervenção da AASPS'}
        url={`/intervencao?servico=${serviceId}`}
      />
      <Header />
      
      <div className="back-button-container">
        <div className="back-button-wrapper">
          <button className="back-button" onClick={() => navigate('/intervencao')} data-testid="back-button">
            <ArrowLeft size={18} />
            Voltar às Áreas de Intervenção
          </button>
        </div>
      </div>

      {/* Hero Image + Content Section */}
      <section className="media-content-section bg-white">
        <div className="media-content-container">
          <div className="media-content-image">
            <img src={service.image} alt={service.title} />
          </div>
          <div className="media-content-text">
            <h2>{service.title}</h2>
            <p>{service.full_content || service.sections?.[0]?.content}</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta-section">
        <div className="cta-container">
          <h2>Fale connosco</h2>
          <p>Entre em contacto para saber mais sobre como podemos ajudar.</p>
          <button className="cta-button" onClick={() => navigate('/contactos')}>
            Contacte-nos
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InterventionPage;
